import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Form, ListGroup } from 'react-bootstrap';

const EMPTY_SEED_DEFAULTS = [];

function mergeDistinctValues(seedDefaults = [], fetched = []) {
  const seen = new Set();
  const merged = [];

  [...seedDefaults, ...fetched].forEach((value) => {
    if (value == null) {
      return;
    }
    const normalized = String(value).trim();
    if (!normalized || seen.has(normalized.toLowerCase())) {
      return;
    }
    seen.add(normalized.toLowerCase());
    merged.push(normalized);
  });

  return merged;
}

function EditListField({
  value,
  onChange,
  label,
  required = false,
  maxLength = 120,
  placeholder = 'Enter or select a value...',
  className = 'mb-3',
  groupClassName,
  valuesUrl,
  seedDefaults = EMPTY_SEED_DEFAULTS,
  active = true,
  refreshKey,
  inputName,
}) {
  const [values, setValues] = useState(() => mergeDistinctValues(seedDefaults, []));
  const [showMatches, setShowMatches] = useState(false);
  const [loading, setLoading] = useState(false);
  const [browseAll, setBrowseAll] = useState(false);
  const seedKey = useMemo(() => JSON.stringify(seedDefaults), [seedDefaults]);

  useEffect(() => {
    if (!active || !valuesUrl) {
      setShowMatches(false);
      setBrowseAll(false);
      if (!valuesUrl) {
        setValues(mergeDistinctValues(seedDefaults, []));
      }
      return undefined;
    }

    let cancelled = false;

    const fetchValues = async () => {
      setLoading(true);
      try {
        const response = await axios.get(valuesUrl);
        if (!cancelled) {
          const fetched = Array.isArray(response.data) ? response.data : [];
          setValues(mergeDistinctValues(seedDefaults, fetched));
        }
      } catch {
        if (!cancelled) {
          setValues(mergeDistinctValues(seedDefaults, []));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchValues();
    return () => {
      cancelled = true;
    };
  }, [active, refreshKey, valuesUrl, seedKey]);

  const filteredValues = useMemo(() => {
    if (browseAll) {
      return values.slice(0, 50);
    }

    const query = (value || '').trim().toLowerCase();
    if (!query) {
      return values.slice(0, 50);
    }

    return values.filter((item) => item.toLowerCase().includes(query)).slice(0, 50);
  }, [values, value, browseAll]);

  const handleSelectValue = (selected) => {
    onChange(selected);
    setBrowseAll(false);
    setShowMatches(false);
  };

  return (
    <Form.Group className={groupClassName || `${className} position-relative`}>
      <Form.Label>
        {label}
        {required ? ' *' : ''}
      </Form.Label>
      <Form.Control
        type="text"
        name={inputName}
        maxLength={maxLength}
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          setBrowseAll(false);
          onChange(e.target.value);
        }}
        onFocus={() => {
          setBrowseAll(true);
          setShowMatches(true);
        }}
        onBlur={() => {
          window.setTimeout(() => {
            setShowMatches(false);
            setBrowseAll(false);
          }, 150);
        }}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        aria-required={required || undefined}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        }}
      />
      {active && showMatches && loading && (
        <div className="small text-muted mt-1">Loading values...</div>
      )}
      {active && showMatches && !loading && filteredValues.length > 0 && (
        <ListGroup
          className="position-absolute w-100 shadow-sm"
          style={{ zIndex: 2, maxHeight: '220px', overflowY: 'auto' }}
        >
          {filteredValues.map((item) => (
            <ListGroup.Item
              key={item}
              action
              active={item === value}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => handleSelectValue(item)}
            >
              {item}
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
      {active &&
        showMatches &&
        !loading &&
        !browseAll &&
        (value || '').trim() &&
        filteredValues.length === 0 && (
          <div className="small text-muted mt-1">
            {values.length > 0 ? 'No matching values' : 'No saved values yet'}
          </div>
        )}
    </Form.Group>
  );
}

export default EditListField;
